import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const MenuCard = ({ item, onAddToCart }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedCustomizations, setSelectedCustomizations] = useState({
    spiceLevel: item.spiceLevel,
    extraSauce: false,
    extraSpice: false
  });

  const allergenIcons = {
    gluten: 'Wheat',
    dairy: 'Milk',
    nuts: 'Nut',
    sesame: 'Circle',
    eggs: 'Egg'
  };

  const spiceLevelColors = {
    none: 'text-gray-400',
    mild: 'text-green-500',
    medium: 'text-yellow-500',
    hot: 'text-orange-500',
    fiery: 'text-red-500'
  };

  const handleAddToCart = () => {
    onAddToCart(item, quantity, selectedCustomizations);
    setQuantity(1);
    setSelectedCustomizations({
      spiceLevel: item.spiceLevel,
      extraSauce: false,
      extraSpice: false
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-warm hover:shadow-warm-lg transition-organic overflow-hidden group">
      {/* Image Container */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={item.image}
          alt={item.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-organic duration-500"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col space-y-2">
          {item.chefRecommended && (
            <span className="bg-accent text-white text-xs font-semibold px-2 py-1 rounded-full flex items-center space-x-1">
              <Icon name="Star" size={12} className="fill-current" />
              <span>Chef's Pick</span>
            </span>
          )}
          {item.seasonal && (
            <span className="bg-trust text-white text-xs font-semibold px-2 py-1 rounded-full flex items-center space-x-1">
              <Icon name="Leaf" size={12} />
              <span>Seasonal</span>
            </span>
          )}
        </div>

        {/* Quick Add Button */}
        <button
          onClick={handleAddToCart}
          className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-primary p-2 rounded-full opacity-0 group-hover:opacity-100 transition-organic hover:bg-white hover:scale-110"
        >
          <Icon name="Plus" size={16} />
        </button>

        {/* Preparation Time */}
        <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center space-x-1">
          <Icon name="Clock" size={12} />
          <span>{item.preparationTime} min</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-headline text-lg font-semibold text-text-primary mb-1 group-hover:text-primary transition-organic">
              {item.name}
            </h3>
            <div className="flex items-center space-x-3 text-sm">
              {/* Dietary Badges */}
              <div className="flex items-center space-x-1">
                {item.dietary.includes('halal') && (
                  <span className="bg-success-100 text-success-700 px-2 py-0.5 rounded text-xs font-medium">
                    Halal
                  </span>
                )}
                {item.dietary.includes('vegetarian') && (
                  <span className="bg-trust-100 text-trust-700 px-2 py-0.5 rounded text-xs font-medium">
                    Vegetarian
                  </span>
                )}
                {item.dietary.includes('vegan') && (
                  <span className="bg-trust-100 text-trust-700 px-2 py-0.5 rounded text-xs font-medium">
                    Vegan
                  </span>
                )}
                {item.dietary.includes('gluten-free') && (
                  <span className="bg-warning-100 text-warning-700 px-2 py-0.5 rounded text-xs font-medium">
                    GF
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-right">
            <span className="font-headline text-xl font-bold text-primary">
              ${item.price}
            </span>
          </div>
        </div>

        {/* Description */}
        <p className="text-text-secondary text-sm mb-4 line-clamp-2">
          {item.description}
        </p>

        {/* Spice Level & Allergens */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Flame" size={16} className={spiceLevelColors[item.spiceLevel]} />
            <span className="text-sm font-medium capitalize text-text-secondary">
              {item.spiceLevel === 'none' ? 'No Spice' : item.spiceLevel}
            </span>
          </div>
          
          {item.allergens.length > 0 && (
            <div className="flex items-center space-x-1">
              <span className="text-xs text-text-secondary">Allergens:</span>
              {item.allergens.map(allergen => (
                <Icon
                  key={allergen}
                  name={allergenIcons[allergen] || 'AlertTriangle'}
                  size={14}
                  className="text-warning-600"
                  title={allergen}
                />
              ))}
            </div>
          )}
        </div>

        {/* Nutritional Info */}
        <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
          <div className="text-center p-2 bg-primary-50 rounded">
            <div className="font-semibold text-primary">{item.nutritional.calories}</div>
            <div className="text-text-secondary">Calories</div>
          </div>
          <div className="text-center p-2 bg-primary-50 rounded">
            <div className="font-semibold text-primary">{item.nutritional.protein}g</div>
            <div className="text-text-secondary">Protein</div>
          </div>
          <div className="text-center p-2 bg-primary-50 rounded">
            <div className="font-semibold text-primary">{item.nutritional.carbs}g</div>
            <div className="text-text-secondary">Carbs</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex-1 bg-primary-100 text-primary font-medium py-2 px-4 rounded-lg hover:bg-primary-200 transition-organic flex items-center justify-center space-x-2"
          >
            <Icon name="Info" size={16} />
            <span>Details</span>
          </button>
          <button
            onClick={handleAddToCart}
            className="flex-1 btn-conversion flex items-center justify-center space-x-2"
          >
            <Icon name="Plus" size={16} />
            <span>Add to Cart</span>
          </button>
        </div>

        {/* Expanded Details */}
        {showDetails && (
          <div className="mt-4 pt-4 border-t border-primary-100 space-y-4">
            {/* Ingredients */}
            <div>
              <h4 className="font-semibold text-text-primary mb-2 flex items-center space-x-2">
                <Icon name="List" size={16} />
                <span>Ingredients</span>
              </h4>
              <div className="flex flex-wrap gap-1">
                {item.ingredients.map(ingredient => (
                  <span
                    key={ingredient}
                    className="bg-surface text-text-secondary text-xs px-2 py-1 rounded"
                  >
                    {ingredient}
                  </span>
                ))}
              </div>
            </div>

            {/* Cultural Context */}
            {item.culturalContext && (
              <div>
                <h4 className="font-semibold text-text-primary mb-2 flex items-center space-x-2">
                  <Icon name="BookOpen" size={16} />
                  <span>Cultural Story</span>
                </h4>
                <p className="text-text-secondary text-sm">
                  {item.culturalContext}
                </p>
              </div>
            )}

            {/* Perfect Pairings */}
            {item.perfectPairings && (
              <div>
                <h4 className="font-semibold text-text-primary mb-2 flex items-center space-x-2">
                  <Icon name="Heart" size={16} />
                  <span>Perfect Pairings</span>
                </h4>
                <div className="flex flex-wrap gap-2">
                  {item.perfectPairings.map(pairing => (
                    <span
                      key={pairing}
                      className="bg-accent-100 text-accent-700 text-xs px-2 py-1 rounded-full"
                    >
                      {pairing}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Customization Options */}
            <div>
              <h4 className="font-semibold text-text-primary mb-2 flex items-center space-x-2">
                <Icon name="Settings" size={16} />
                <span>Customize Your Order</span>
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Quantity</span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-8 h-8 rounded-full bg-primary-100 text-primary flex items-center justify-center hover:bg-primary-200 transition-organic"
                    >
                      <Icon name="Minus" size={14} />
                    </button>
                    <span className="w-8 text-center font-medium">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-8 h-8 rounded-full bg-primary-100 text-primary flex items-center justify-center hover:bg-primary-200 transition-organic"
                    >
                      <Icon name="Plus" size={14} />
                    </button>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Extra Sauce</span>
                  <button
                    onClick={() => setSelectedCustomizations(prev => ({ ...prev, extraSauce: !prev.extraSauce }))}
                    className={`w-12 h-6 rounded-full transition-organic ${
                      selectedCustomizations.extraSauce ? 'bg-primary' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-organic ${
                      selectedCustomizations.extraSauce ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuCard;